// R5.5 — THE REAL HIDDEN-REGION DETECTOR (codebook §11.3; spec §7 T-HIDE-1; the C4 parse boundary).
//
// ── WHAT THIS CLOSES ─────────────────────────────────────────────────────────────────────────────────────────
// `factExtract.ts`'s C4 note has said since R2 that detection CANNOT live in the worker, because by the time text
// reaches the worker its styling — the only thing that makes it hidden — was discarded upstream. That note was
// right about the ARCHITECTURE and it named the missing piece: "the client-side style detector that actually
// produces these annotations from a .docx/.pdf upload". Until R5.5 that piece did not exist, so the only hidden
// regions the pipeline ever saw were the ones a fixture marked by hand, and T-HIDE-1 exercised the chain from a
// FABRICATED annotation rather than from a document. The R5.5 build review named that as blocking finding 1.
//
// This module is the missing piece. The chain is unchanged and deliberately so:
//
//     THIS MODULE (styling still exists)  →  HiddenRegionAnnotation[]
//     annotateHiddenRegions()             →  [[HIDDEN:detector]] … [[/HIDDEN]] sentinels
//     sanitizeDocument()                  →  canonical text with the region DELETED, region reported
//     extractFacts()                      →  no hidden claim can source a fact (offsets are post-strip)
//     extraction_uncertain                →  CS-FLAG-EXTRACTION-UNCERTAIN, capped at Moderate confidence
//
// Only the SOURCE of the annotations changed. Everything downstream keeps the coordinate system and the offset
// math it already had, which is why this is an addition rather than a rewrite.
//
// ── WHY IT LIVES IN `workers/src/lib` AND RUNS IN THE CLIENT ─────────────────────────────────────────────────
// Exactly the `bannedOutputRegistry.ts` precedent: one module, imported by both halves, so the two cannot drift.
// The DETECTION runs in the browser, where the uploaded bytes are; only extracted TEXT plus annotations ever
// leave it. There is no upload route on the worker and this module does not add one.
//
// ── ZERO NEW DEPENDENCIES, ON PURPOSE ────────────────────────────────────────────────────────────────────────
// A .docx is a ZIP of XML. Rather than add a zip library to the client bundle (and rather than lean on mammoth's
// transitive JSZip, which is an implicit dependency that a mammoth minor could remove), this file reads the ZIP
// central directory itself and inflates entries with the platform's own `DecompressionStream('deflate-raw')` —
// present in every browser the product supports and in Node 18+, so the SAME code path runs in the suite.
//
// ── HONEST STATEMENT OF PER-FORMAT REACH (see `DETECTOR_COVERAGE` at the foot of this file) ──────────────────
// DOCX detection is REAL and structural: the styling is in the file and this module reads it. PDF detection is
// BEST-EFFORT and its boundary is precise rather than vague — `getTextContent()` exposes geometry and glyphs but
// NOT fill colour and NOT text render mode, so white-on-white and `3 Tr` invisible text are outside what the
// pinned pdf.js API can report. Those two are named as an explicit boundary rather than silently missed.

/* ============================================================================================================== *
 * THE PARSE-BOUNDARY CONTRACT ITSELF
 *
 * These two declarations lived in `factExtract.ts` from R2 until R5.5, back when the detector was a boundary note
 * rather than a module. They belong HERE: this is the file that produces annotations, and it is the file the
 * client imports. `factExtract.ts` re-exports both, so every existing importer is unaffected.
 * ============================================================================================================== */

export interface HiddenRegionAnnotation {
  /** Which detector fired, e.g. `font-size-near-zero=1pt`, `color-matches-background=#FFFFFF-on-#FFFFFF`. */
  detector: string;
  /** Code-point offsets into the parser's own extracted text, BEFORE any normalization. */
  cp_start: number;
  cp_end: number;
}

/**
 * The server side of the parse-boundary contract: take the parser's plain text plus its hidden-region annotations
 * and return text carrying the sentinels `sanitizeDocument` strips. Splitting mark-then-strip keeps ONE stripping
 * implementation and one coordinate system, whatever the detector was.
 *
 * Annotations are applied back-to-front so an earlier insertion cannot invalidate a later offset.
 */
export function annotateHiddenRegions(text: string, annotations: readonly HiddenRegionAnnotation[]): string {
  const cps = Array.from(text);
  const ordered = [...annotations].sort((a, b) => b.cp_start - a.cp_start);
  let out = cps;
  for (const a of ordered) {
    if (!Number.isInteger(a.cp_start) || !Number.isInteger(a.cp_end)) continue;
    if (a.cp_start < 0 || a.cp_end > out.length || a.cp_end <= a.cp_start) continue;
    out = [
      ...out.slice(0, a.cp_start),
      `[[HIDDEN:${a.detector.replace(/[[\]]/g, '')}]]`,
      ...out.slice(a.cp_start, a.cp_end),
      '[[/HIDDEN]]',
      ...out.slice(a.cp_end),
    ];
  }
  return out.join('');
}

/* ============================================================================================================== *
 * DETECTOR IDS — the closed set. `sanitizeDocument` is agnostic about which fired (it reads only the marker), but
 * the ids are pinned here so telemetry has a stable vocabulary and a test can assert on them by name.
 * ============================================================================================================== */
export const HIDDEN_DETECTORS = {
  VANISH: 'w:vanish',
  WEB_HIDDEN: 'w:webHidden',
  COLOR_ON_BACKGROUND: 'color-matches-background',
  TINY_FONT: 'font-size-near-zero',
  OFF_CANVAS: 'off-canvas',
  ZERO_WIDTH: 'zero-width-run',
} as const;

/** Half-points. OOXML `w:sz` is in half-points, so 2pt is 4. §11.3's "zero/near-zero font size". */
export const TINY_FONT_HALF_POINTS = 4;

/** Manhattan distance in 8-bit RGB below which a text colour counts as "matching" its background. */
export const COLOR_MATCH_TOLERANCE = 24;

// Zero-width and format/control code points, written as ESCAPES rather than as the characters themselves:
// a literal U+200B in source is invisible to a reviewer, which is precisely the property this rule is about.
const ZERO_WIDTH_RE = /^[\u200B-\u200F\u202A-\u202E\u2060-\u2064\uFEFF\u00AD\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]+$/u;

/* ============================================================================================================== *
 * A MINIMAL ZIP READER
 *
 * Enough of the format to pull named entries out of a .docx, and no more: the end-of-central-directory record,
 * the central directory, and STORE (0) / DEFLATE (8) entries. Anything else — encrypted, spanned, a method we do
 * not know — is reported as unreadable rather than guessed at, because a detector that silently returns "no
 * hidden regions" for a file it could not open is worse than one that says it could not open the file.
 * ============================================================================================================== */

class ZipReadError extends Error {}

async function inflateRaw(bytes: Uint8Array): Promise<Uint8Array> {
  const ds = new DecompressionStream('deflate-raw');
  // `new Response(bytes).body` rather than a Blob: Blob's constructor signature needs the DOM lib's `BlobPart`,
  // and this module is compiled against the worker's types as well as the browser's.
  // `.slice().buffer` gives a plain ArrayBuffer: `bytes` is a subarray VIEW into the archive, and a view is not
  // a `BodyInit` under the DOM lib's typing.
  const body = new Response(bytes.slice().buffer as ArrayBuffer).body;
  if (!body) throw new ZipReadError('cannot read compressed entry');
  return new Uint8Array(await new Response(body.pipeThrough(ds)).arrayBuffer());
}

async function readZipEntries(buf: ArrayBuffer): Promise<Map<string, Uint8Array>> {
  const u8 = new Uint8Array(buf);
  const dv = new DataView(buf);
  // Locate the End Of Central Directory record by scanning backwards for its signature. The comment field is at
  // most 65535 bytes, so the scan is bounded.
  let eocd = -1;
  for (let i = u8.length - 22; i >= Math.max(0, u8.length - 22 - 65535); i--) {
    if (dv.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  }
  if (eocd < 0) throw new ZipReadError('not a zip archive (no end-of-central-directory record)');

  const count = dv.getUint16(eocd + 10, true);
  let p = dv.getUint32(eocd + 16, true);
  const out = new Map<string, Uint8Array>();
  const decoder = new TextDecoder('utf-8');

  for (let i = 0; i < count; i++) {
    if (dv.getUint32(p, true) !== 0x02014b50) throw new ZipReadError('corrupt central directory');
    const method = dv.getUint16(p + 10, true);
    const compressedSize = dv.getUint32(p + 20, true);
    const nameLen = dv.getUint16(p + 28, true);
    const extraLen = dv.getUint16(p + 30, true);
    const commentLen = dv.getUint16(p + 32, true);
    const localOffset = dv.getUint32(p + 42, true);
    const name = decoder.decode(u8.subarray(p + 46, p + 46 + nameLen));
    p += 46 + nameLen + extraLen + commentLen;

    // The local header repeats the name/extra lengths, and its extra field can differ from the central one.
    if (dv.getUint32(localOffset, true) !== 0x04034b50) throw new ZipReadError(`corrupt local header for ${name}`);
    const lNameLen = dv.getUint16(localOffset + 26, true);
    const lExtraLen = dv.getUint16(localOffset + 28, true);
    const dataStart = localOffset + 30 + lNameLen + lExtraLen;
    const raw = u8.subarray(dataStart, dataStart + compressedSize);

    if (method === 0) out.set(name, raw);
    else if (method === 8) out.set(name, await inflateRaw(raw));
    else throw new ZipReadError(`unsupported compression method ${method} for ${name}`);
  }
  return out;
}

/* ============================================================================================================== *
 * A MINIMAL OOXML READER
 *
 * WordprocessingML is regular enough for the narrow question this module asks — "which runs are hidden, and where
 * is their text in the string we are about to hand over?" — that a tokenizing walk is the right tool. It is NOT a
 * general XML parser and does not pretend to be: it reads elements, attributes and text, and it does not resolve
 * entities beyond the five predefined ones or handle DTDs, because a .docx contains none.
 * ============================================================================================================== */

interface XmlToken {
  kind: 'open' | 'close' | 'selfclose' | 'text';
  name: string;
  attrs: Record<string, string>;
  text: string;
}

function decodeEntities(s: string): string {
  return s.replace(/&(#x?[0-9a-fA-F]+|amp|lt|gt|quot|apos);/g, (m, e: string) => {
    if (e === 'amp') return '&';
    if (e === 'lt') return '<';
    if (e === 'gt') return '>';
    if (e === 'quot') return '"';
    if (e === 'apos') return "'";
    if (e.startsWith('#x') || e.startsWith('#X')) return String.fromCodePoint(parseInt(e.slice(2), 16));
    if (e.startsWith('#')) return String.fromCodePoint(parseInt(e.slice(1), 10));
    return m;
  });
}

function* tokenize(xml: string): Generator<XmlToken> {
  const re = /<!--[\s\S]*?-->|<\?[\s\S]*?\?>|<!\[CDATA\[([\s\S]*?)\]\]>|<(\/?)([A-Za-z_][\w.:-]*)((?:\s+[\w.:-]+\s*=\s*(?:"[^"]*"|'[^']*'))*)\s*(\/?)>/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(xml)) !== null) {
    if (m.index > last) {
      const t = xml.slice(last, m.index);
      if (t.length > 0) yield { kind: 'text', name: '', attrs: {}, text: decodeEntities(t) };
    }
    last = re.lastIndex;
    if (m[1] !== undefined) { yield { kind: 'text', name: '', attrs: {}, text: m[1] }; continue; }
    if (m[3] === undefined) continue; // comment / processing instruction
    const attrs: Record<string, string> = {};
    for (const a of (m[4] ?? '').matchAll(/([\w.:-]+)\s*=\s*(?:"([^"]*)"|'([^']*)')/g)) {
      attrs[a[1]] = decodeEntities(a[2] ?? a[3] ?? '');
    }
    yield {
      kind: m[2] === '/' ? 'close' : m[5] === '/' ? 'selfclose' : 'open',
      name: m[3], attrs, text: '',
    };
  }
  if (last < xml.length) {
    const t = xml.slice(last);
    if (t.length > 0) yield { kind: 'text', name: '', attrs: {}, text: decodeEntities(t) };
  }
}

/** Strip the namespace prefix. Word writes `w:`, but a Pages/Docs export may bind the namespace differently. */
const local = (name: string) => name.replace(/^[^:]*:/, '');

/* ============================================================================================================== *
 * RUN PROPERTIES — the styling that decides "hidden"
 * ============================================================================================================== */

interface RunProps {
  vanish?: boolean;
  webHidden?: boolean;
  /** `w:sz` in HALF-points, as OOXML stores it. */
  halfPoints?: number;
  /** Six hex digits, upper case, or 'AUTO'. */
  color?: string;
  /** `w:shd`'s `w:fill` — the run's own background, when it sets one. */
  fill?: string;
  styleId?: string;
}

/** An OOXML boolean toggle: present means on unless it says otherwise. */
function toggle(attrs: Record<string, string>): boolean {
  const v = attrs['w:val'] ?? attrs['val'];
  return v === undefined || !['0', 'false', 'off'].includes(v.toLowerCase());
}

function normalizeColor(v: string | undefined): string | undefined {
  if (v === undefined) return undefined;
  const t = v.trim().toUpperCase().replace(/^#/, '');
  if (t === 'AUTO') return 'AUTO';
  return /^[0-9A-F]{6}$/.test(t) ? t : undefined;
}

/** Read one `<w:rPr>` element's children into a `RunProps`. The token stream is consumed to the matching close. */
function readRunProps(tokens: XmlToken[], start: number): { props: RunProps; next: number } {
  const props: RunProps = {};
  let i = start;
  let depth = 0;
  for (; i < tokens.length; i++) {
    const t = tokens[i];
    if (t.kind === 'open' && local(t.name) === 'rPr') { depth += 1; continue; }
    if (t.kind === 'close' && local(t.name) === 'rPr') { depth -= 1; if (depth <= 0) { i += 1; break; } continue; }
    if (t.kind !== 'open' && t.kind !== 'selfclose') continue;
    switch (local(t.name)) {
      case 'vanish': props.vanish = toggle(t.attrs); break;
      case 'webHidden': props.webHidden = toggle(t.attrs); break;
      case 'sz': case 'szCs': {
        const n = Number(t.attrs['w:val'] ?? t.attrs['val']);
        if (Number.isFinite(n)) props.halfPoints = Math.min(props.halfPoints ?? n, n);
        break;
      }
      case 'color': props.color = normalizeColor(t.attrs['w:val'] ?? t.attrs['val']); break;
      case 'shd': props.fill = normalizeColor(t.attrs['w:fill'] ?? t.attrs['fill']); break;
      case 'rStyle': props.styleId = t.attrs['w:val'] ?? t.attrs['val']; break;
      default: break;
    }
  }
  return { props, next: i };
}

/** Later wins, but only where it actually sets something — that is what inheritance means. */
function mergeProps(...layers: (RunProps | undefined)[]): RunProps {
  const out: RunProps = {};
  for (const l of layers) {
    if (!l) continue;
    if (l.vanish !== undefined) out.vanish = l.vanish;
    if (l.webHidden !== undefined) out.webHidden = l.webHidden;
    if (l.halfPoints !== undefined) out.halfPoints = l.halfPoints;
    if (l.color !== undefined) out.color = l.color;
    if (l.fill !== undefined) out.fill = l.fill;
    if (l.styleId !== undefined) out.styleId = l.styleId;
  }
  return out;
}

/**
 * ONE ENTRY OF `styles.xml`: its own run properties, its `w:basedOn` parent, and — for a paragraph style — the
 * paragraph shading it sets. Kept UNRESOLVED here; resolution happens in `resolveStyleChain` so that a cycle in
 * the file cannot become a cycle in this parser.
 */
interface StyleDef {
  id: string;
  /** `w:type` — 'character' | 'paragraph' | … . Recorded but not gated on: Word resolves whatever is named. */
  type?: string;
  /** `w:basedOn` — the parent this style inherits from. */
  basedOn?: string;
  /** The style's own `w:rPr`. */
  rPr: RunProps;
  /** A paragraph style's `w:pPr/w:shd` fill — the background layer it puts behind its paragraphs. */
  paraShd?: string;
}

/**
 * Parse `styles.xml` into a map of style-id → definition, plus the document defaults.
 *
 * THIS MATTERS AND IS NOT GOLD-PLATING: the cheapest way to hide text in Word without touching a single run is to
 * define a character style that is white or `vanish` and apply it. A detector that only read inline `w:rPr` would
 * report such a document as clean, which is precisely the case worth catching.
 *
 * AND THE SECOND ROUND OF THAT SAME EVASION is `w:basedOn`: a style whose own `w:rPr` is empty but whose PARENT
 * vanishes is, in Word, a vanishing style. A parser that read only the named style's own properties would call
 * such a run clean while reporting `styling_inspected: true` — a confident wrong answer, which is worse than an
 * honest "not inspected". So the chain is stored here and walked in `resolveStyleChain`.
 */
function parseStyles(xml: string): { byId: Map<string, StyleDef>; docDefaults: RunProps } {
  const tokens = [...tokenize(xml)];
  const byId = new Map<string, StyleDef>();
  let docDefaults: RunProps = {};
  let current: StyleDef | undefined;
  let inDocDefaults = false;
  let pPrDepth = 0;
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    const name = local(t.name);
    if (t.kind === 'open' && name === 'docDefaults') { inDocDefaults = true; continue; }
    if (t.kind === 'close' && name === 'docDefaults') { inDocDefaults = false; continue; }
    if (t.kind === 'open' && name === 'style') {
      const id = t.attrs['w:styleId'] ?? t.attrs['styleId'];
      current = id ? (byId.get(id) ?? { id, type: t.attrs['w:type'] ?? t.attrs['type'], rPr: {} }) : undefined;
      if (current) byId.set(current.id, current);
      pPrDepth = 0;
      continue;
    }
    if (t.kind === 'close' && name === 'style') { current = undefined; pPrDepth = 0; continue; }
    if ((t.kind === 'open' || t.kind === 'selfclose') && name === 'basedOn' && current) {
      current.basedOn = t.attrs['w:val'] ?? t.attrs['val'];
      continue;
    }
    if (t.kind === 'open' && name === 'pPr') { pPrDepth += 1; continue; }
    if (t.kind === 'close' && name === 'pPr') { pPrDepth -= 1; continue; }
    // `w:pPr/w:shd` on a PARAGRAPH STYLE is a background layer, not a run property — see `effectiveBackground`.
    if ((t.kind === 'open' || t.kind === 'selfclose') && name === 'shd' && pPrDepth > 0 && current) {
      const fill = normalizeColor(t.attrs['w:fill'] ?? t.attrs['fill']);
      if (fill !== undefined) current.paraShd = fill;
      continue;
    }
    if (t.kind === 'open' && name === 'rPr') {
      const { props, next } = readRunProps(tokens, i);
      if (inDocDefaults) docDefaults = mergeProps(docDefaults, props);
      else if (current) current.rPr = mergeProps(current.rPr, props);
      i = next - 1;
    }
  }
  return { byId, docDefaults };
}

/** How deep a `w:basedOn` chain is followed before the file is treated as pathological and the walk stops. */
export const MAX_STYLE_CHAIN_DEPTH = 32;

/**
 * Resolve a style id through its `w:basedOn` chain, ROOT FIRST so the nearer style wins, and CYCLE-SAFE: a file
 * whose styles are based on each other (A→B→A, or a self-reference) must not hang the detector. A visited-set
 * plus a depth cap gives both, and stopping early degrades to "resolved as far as we could" rather than to a
 * crash — the annotations already found stay valid either way.
 */
function resolveStyleChain(
  byId: Map<string, StyleDef>,
  id: string | undefined,
): { rPr: RunProps; paraShd?: string } {
  const chain: StyleDef[] = [];
  const seen = new Set<string>();
  let cursor = id;
  while (cursor !== undefined && !seen.has(cursor) && chain.length < MAX_STYLE_CHAIN_DEPTH) {
    seen.add(cursor);
    const def = byId.get(cursor);
    if (!def) break;
    chain.push(def);
    cursor = def.basedOn;
  }
  // `chain` runs leaf → root; merge root → leaf so the MORE SPECIFIC style is applied last and wins.
  let rPr: RunProps = {};
  let paraShd: string | undefined;
  for (let i = chain.length - 1; i >= 0; i--) {
    rPr = mergeProps(rPr, chain[i].rPr);
    if (chain[i].paraShd !== undefined) paraShd = chain[i].paraShd;
  }
  // A style's own `w:rStyle`, were one present, is not an inheritance edge — drop it so it cannot be mistaken
  // for the run's own `w:rStyle` downstream.
  delete rPr.styleId;
  return { rPr, paraShd };
}

/**
 * THE EFFECTIVE BACKGROUND BEHIND ONE RUN, most specific layer first:
 *
 *     run w:shd fill  >  paragraph w:shd fill (direct or from the paragraph style chain)  >  w:background  >  white
 *
 * Reading only the first and third of those was unsafe in both directions, and both directions matter: white text
 * on a dark-shaded PARAGRAPH is ordinary visible design and must be KEPT (stripping it deletes the applicant's own
 * words), while dark text on that same dark-shaded paragraph is hidden and must be FLAGGED. The layer that decides
 * which of the two it is is the paragraph shading, so it has to be read.
 */
export function effectiveBackground(layers: {
  runFill?: string;
  paragraphFill?: string;
  pageBackground?: string;
}): string {
  const pick = (v: string | undefined) => (v !== undefined && v !== 'AUTO' ? v : undefined);
  return pick(layers.runFill) ?? pick(layers.paragraphFill) ?? pick(layers.pageBackground) ?? 'FFFFFF';
}

/** The document-level page background, when `document.xml` declares one. Default is white. */
function documentBackground(xml: string): string {
  const m = xml.match(/<w:background\b[^>]*\bw:color\s*=\s*"([^"]*)"/i);
  return normalizeColor(m?.[1]) ?? 'FFFFFF';
}

function colorDistance(a: string, b: string): number {
  const rgb = (h: string) => [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  const [r1, g1, b1] = rgb(a);
  const [r2, g2, b2] = rgb(b);
  return Math.abs(r1 - r2) + Math.abs(g1 - g2) + Math.abs(b1 - b2);
}

/**
 * WHICH DETECTOR, IF ANY, FIRES ON ONE RUN.
 *
 * Returns the detector id, or `null` for an ordinary visible run. Order is by CONFIDENCE, not by severity: an
 * explicit `w:vanish` is a statement of intent by the file and is reported as such even if the run also happens
 * to be small.
 */
export function detectorForRun(
  props: RunProps,
  ctx: { background: string; framePr?: { offPage: boolean } },
  text: string,
): string | null {
  if (props.vanish) return HIDDEN_DETECTORS.VANISH;
  if (props.webHidden) return HIDDEN_DETECTORS.WEB_HIDDEN;
  if (props.halfPoints !== undefined && props.halfPoints <= TINY_FONT_HALF_POINTS) {
    return `${HIDDEN_DETECTORS.TINY_FONT}=${props.halfPoints / 2}pt`;
  }
  // `ctx.background` is ALREADY the paragraph-or-page layer (see `effectiveBackground`); the run's own w:shd
  // fill, when it sets one, sits on top of it.
  const bg = effectiveBackground({ runFill: props.fill, pageBackground: ctx.background });
  if (props.color && props.color !== 'AUTO' && bg !== 'AUTO' && colorDistance(props.color, bg) <= COLOR_MATCH_TOLERANCE) {
    return `${HIDDEN_DETECTORS.COLOR_ON_BACKGROUND}=#${props.color}-on-#${bg}`;
  }
  if (ctx.framePr?.offPage) return HIDDEN_DETECTORS.OFF_CANVAS;
  // A run made only of zero-width or control code points renders as nothing and can carry a payload. Note the
  // guard: text that merely CONTAINS a zero-width character is left alone — deleting a region because one
  // invisible code point sits inside real prose would eat the applicant's own words.
  if (text.length > 0 && ZERO_WIDTH_RE.test(text)) return HIDDEN_DETECTORS.ZERO_WIDTH;
  return null;
}

/** What the detector hands back: the text as extracted, plus the regions of it that are hidden. */
export interface DetectedDocument {
  /** The extracted plain text, INCLUDING the hidden runs. Annotation offsets are code-point offsets into it. */
  text: string;
  annotations: HiddenRegionAnnotation[];
  /** Which format the detection ran against, and how complete it can be. See `DETECTOR_COVERAGE`. */
  format: 'docx' | 'pdf' | 'text';
  /**
   * TRUE when the detector ran and can vouch for its answer; FALSE when it fell back to a text-only read (an
   * unreadable archive, a format with no styling). A caller must not read `annotations: []` as "nothing hidden"
   * unless this is true.
   */
  styling_inspected: boolean;
}

/* ============================================================================================================== *
 * DOCX
 * ============================================================================================================== */

/**
 * Extract text from `word/document.xml` AND annotate its hidden regions IN ONE WALK.
 *
 * The single walk is the whole design. Extraction and detection must agree on offsets to the code point, and two
 * passes — mammoth for text, a separate parse for styling — could not, because they make different decisions
 * about tabs, breaks, table cells and paragraph joins. Whatever THIS function decides the text is, the offsets
 * are into that, by construction.
 */
export async function detectDocxHiddenRegions(buf: ArrayBuffer): Promise<DetectedDocument> {
  let entries: Map<string, Uint8Array>;
  try {
    entries = await readZipEntries(buf);
  } catch {
    return { text: '', annotations: [], format: 'docx', styling_inspected: false };
  }
  const decoder = new TextDecoder('utf-8');
  const docBytes = entries.get('word/document.xml');
  if (!docBytes) return { text: '', annotations: [], format: 'docx', styling_inspected: false };
  const documentXml = decoder.decode(docBytes);
  const stylesBytes = entries.get('word/styles.xml');
  const { byId: styleProps, docDefaults } = stylesBytes
    ? parseStyles(decoder.decode(stylesBytes))
    : { byId: new Map<string, StyleDef>(), docDefaults: {} as RunProps };
  const pageBackground = documentBackground(documentXml);

  const tokens = [...tokenize(documentXml)];
  const pieces: string[] = [];
  let cp = 0; // code-point cursor into the text being built
  const annotations: HiddenRegionAnnotation[] = [];

  /** Paragraph-level run properties (`w:pPr/w:rPr`) and frame position, inherited by the paragraph's runs. */
  let paraProps: RunProps = {};
  let paraOffPage = false;
  /** The paragraph's own `w:pPr/w:shd` fill — the background layer between the run's shd and the page. */
  let paraShd: string | undefined;
  /** The paragraph's `w:pStyle`, resolved through `w:basedOn` for BOTH its rPr and its paragraph shading. */
  let paraStyleId: string | undefined;
  let runProps: RunProps = {};
  let inRun = false;
  /** Depth inside `w:pPr`, so a `w:shd` can be told apart from one in a table or a run's own properties. */
  let pPrDepth = 0;
  let preserveSpace = false;
  let firstParagraph = true;

  const emit = (s: string, detector: string | null) => {
    if (s.length === 0) return;
    const len = Array.from(s).length;
    if (detector) {
      const prev = annotations[annotations.length - 1];
      // Coalesce runs that are adjacent AND hidden the same way, so a sentence split across three formatting
      // runs is one region rather than three.
      if (prev && prev.detector === detector && prev.cp_end === cp) prev.cp_end = cp + len;
      else annotations.push({ detector, cp_start: cp, cp_end: cp + len });
    }
    pieces.push(s);
    cp += len;
  };

  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    const name = local(t.name);

    if (t.kind === 'open' && name === 'p') {
      if (!firstParagraph) emit('\n', null);
      firstParagraph = false;
      paraProps = {};
      paraOffPage = false;
      paraShd = undefined;
      paraStyleId = undefined;
      pPrDepth = 0;
      continue;
    }
    if (t.kind === 'open' && name === 'pPr') { pPrDepth += 1; continue; }
    if (t.kind === 'close' && name === 'pPr') { pPrDepth -= 1; continue; }
    if ((t.kind === 'open' || t.kind === 'selfclose') && name === 'pStyle' && pPrDepth > 0) {
      paraStyleId = t.attrs['w:val'] ?? t.attrs['val'];
      continue;
    }
    // The PARAGRAPH's shading. Guarded on `pPrDepth` so a table's or a run's `w:shd` cannot be read as one —
    // a run's own `w:shd` never reaches here, because `readRunProps` consumes it inside `w:rPr`.
    if ((t.kind === 'open' || t.kind === 'selfclose') && name === 'shd' && pPrDepth > 0 && !inRun) {
      const fill = normalizeColor(t.attrs['w:fill'] ?? t.attrs['fill']);
      if (fill !== undefined) paraShd = fill;
      continue;
    }
    if (t.kind === 'open' && name === 'r') { inRun = true; runProps = {}; continue; }
    if (t.kind === 'close' && name === 'r') { inRun = false; runProps = {}; continue; }

    // A `w:framePr` with a text-frame anchored well outside the printable page is §11.3's "off-canvas
    // positioning". Word stores these in TWIPS (1/1440 inch); a page is at most ~15840 twips tall, so a frame
    // pushed beyond that, or far negative, is off the page rather than merely indented.
    if ((t.kind === 'open' || t.kind === 'selfclose') && name === 'framePr') {
      const x = Number(t.attrs['w:x'] ?? t.attrs['x']);
      const y = Number(t.attrs['w:y'] ?? t.attrs['y']);
      const off = (v: number) => Number.isFinite(v) && (v < -1440 || v > 15840);
      if (off(x) || off(y)) paraOffPage = true;
      continue;
    }

    if (t.kind === 'open' && name === 'rPr') {
      const { props, next } = readRunProps(tokens, i);
      if (inRun) runProps = props;
      else paraProps = props; // a `w:pPr/w:rPr` — the paragraph mark's own run properties, inherited by its runs
      i = next - 1;
      continue;
    }

    if ((t.kind === 'open' || t.kind === 'selfclose') && name === 't') {
      preserveSpace = (t.attrs['xml:space'] ?? t.attrs['space']) === 'preserve';
      continue;
    }
    if ((t.kind === 'open' || t.kind === 'selfclose') && name === 'tab') {
      emit('\t', effectiveDetector(''));
      continue;
    }
    if ((t.kind === 'open' || t.kind === 'selfclose') && (name === 'br' || name === 'cr')) {
      emit('\n', null);
      continue;
    }

    if (t.kind === 'text' && inRun) {
      // Only text that is actually inside a `<w:t>` is document text. `<w:instrText>` (field codes), `<w:delText>`
      // (tracked deletions) and whitespace between elements are not, and emitting them would put strings in the
      // canonical text that the reader never sees — which is the very defect this module exists to catch.
      const prev = tokens[i - 1];
      if (!prev || prev.kind !== 'open' || local(prev.name) !== 't') continue;
      const s = preserveSpace ? t.text : t.text;
      emit(s, effectiveDetector(s));
      continue;
    }
  }

  /**
   * THE FULL INHERITANCE RESOLUTION for one run, least specific layer first:
   *
   *     docDefaults  <  w:pStyle chain (w:basedOn resolved)  <  w:pPr/w:rPr  <  w:rStyle chain  <  the run's own rPr
   *
   * and, in parallel, the background layers `effectiveBackground` orders. Both chains are walked through
   * `w:basedOn`, so a style that inherits its hiding from a grandparent is resolved rather than missed.
   */
  function effectiveDetector(text: string): string | null {
    const pStyle = resolveStyleChain(styleProps, paraStyleId);
    const rStyle = resolveStyleChain(styleProps, runProps.styleId);
    const props = mergeProps(docDefaults, pStyle.rPr, paraProps, rStyle.rPr, runProps);
    const background = effectiveBackground({
      paragraphFill: paraShd ?? pStyle.paraShd,
      pageBackground,
    });
    return detectorForRun(props, { background, framePr: { offPage: paraOffPage } }, text);
  }

  return { text: pieces.join(''), annotations, format: 'docx', styling_inspected: true };
}

/* ============================================================================================================== *
 * PDF — BEST EFFORT, WITH A BOUNDARY THAT IS STATED RATHER THAN IMPLIED
 * ============================================================================================================== */

/**
 * The subset of a pdf.js text item this detector reads. Abstracted deliberately: the detection RULES are then a
 * pure function that the suite can test without loading pdf.js, and the pdf.js version can move without moving
 * the rules.
 */
export interface PdfTextItem {
  str: string;
  /** pdf.js's 6-element transform: [a, b, c, d, e, f]. `d` is the vertical scale ≈ the rendered font size. */
  transform: readonly number[];
  /** Rendered height in PDF units, when pdf.js supplies it. */
  height?: number;
}

/** Below this rendered size (PDF points) a text item is §11.3's "near-zero font size". */
export const PDF_TINY_FONT_POINTS = 2;

/**
 * Annotate the hidden regions of a PDF page's text items, over the SAME joining rule `cvParse.ts` uses. Offsets
 * are code-point offsets into the string this function returns.
 *
 * `separators`, when given, is the string placed BEFORE each item (`separators[0]` is ignored — nothing precedes
 * the first item); it is how `cvParse.ts` hands over the line / column structure its layout reconstruction
 * (`client/src/lib/pdfLayout.ts`) derived from the page geometry, so the text the detector reports offsets INTO
 * is the very text the app keeps. Without it, items are joined by a single space (the legacy flat join).
 */
export function detectPdfHiddenRegions(
  items: readonly PdfTextItem[],
  pageBox: { width: number; height: number },
  startCp = 0,
  separators?: readonly string[],
): { text: string; annotations: HiddenRegionAnnotation[] } {
  const parts: string[] = [];
  const annotations: HiddenRegionAnnotation[] = [];
  let cp = startCp;
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    const s = it.str ?? '';
    if (i > 0) {
      const sep = separators ? (separators[i] ?? ' ') : ' ';
      if (sep) { parts.push(sep); cp += Array.from(sep).length; }
    }
    const len = Array.from(s).length;
    const size = Math.abs(it.height ?? it.transform?.[3] ?? 0);
    const x = it.transform?.[4] ?? 0;
    const y = it.transform?.[5] ?? 0;
    let detector: string | null = null;
    // A LONE BULLET / PUNCTUATION GLYPH IS NOT HIDDEN TEXT, whatever its reported size. Some generators (FlowCV
    // among them) draw the bullet marker as its own text item with a sub-2pt vertical scale; flagging it would
    // strip every bullet marker from the CV and raise `extraction_uncertain` on a perfectly honest document.
    // A single non-alphanumeric glyph carries no claim, so it cannot be a hidden one.
    const loneGlyph = len === 1 && !/[\p{L}\p{N}]/u.test(s);
    if (len > 0 && size > 0 && size <= PDF_TINY_FONT_POINTS && !loneGlyph) detector = `${HIDDEN_DETECTORS.TINY_FONT}=${size.toFixed(2)}pt`;
    else if (len > 0 && (x < -size || y < -size || x > pageBox.width + size || y > pageBox.height + size)) detector = HIDDEN_DETECTORS.OFF_CANVAS;
    else if (len > 0 && ZERO_WIDTH_RE.test(s)) detector = HIDDEN_DETECTORS.ZERO_WIDTH;
    if (detector) {
      const prev = annotations[annotations.length - 1];
      if (prev && prev.detector === detector && prev.cp_end === cp) prev.cp_end = cp + len;
      else annotations.push({ detector, cp_start: cp, cp_end: cp + len });
    }
    parts.push(s);
    cp += len;
  }
  return { text: parts.join(''), annotations };
}

/* ============================================================================================================== *
 * THE COVERAGE TABLE — what each format's detector CAN and CANNOT see.
 *
 * Exported and asserted in `tests/hiddenRegionDetect.test.ts` so it cannot quietly go stale, and reproduced in
 * spec §13 as the T-HIDE-1 boundary note. A boundary that lives only in a comment is a boundary nobody is held to.
 * ============================================================================================================== */
export const DETECTOR_COVERAGE = {
  docx: {
    detects: [
      'w:vanish — the file explicitly marks the run hidden',
      'w:webHidden — hidden in web view',
      'w:sz ≤ 4 half-points (2pt) — zero/near-zero font size, inline or inherited from a character or paragraph style',
      'w:color within 24 (Manhattan RGB) of the EFFECTIVE BACKGROUND — white-on-white and its near neighbours. The background is layered, most specific first: the run\'s own w:shd fill, else the paragraph\'s w:pPr/w:shd fill (direct or from its w:pStyle chain), else the document w:background, else white. Read both ways: white text on a dark-shaded paragraph is KEPT, dark text on that same paragraph is FLAGGED.',
      'w:framePr positioned beyond the printable page (< -1440 or > 15840 twips) — off-canvas',
      'runs consisting ENTIRELY of zero-width or control code points',
      'all of the above when inherited from word/styles.xml — the w:rStyle chain, the w:pStyle chain and docDefaults are all resolved, and each chain is followed THROUGH w:basedOn (cycle-safe, depth-capped), so a style that inherits its hiding from a grandparent is caught rather than reported clean',
      'precedence, applied in this order: the run\'s own w:rPr beats its w:rStyle chain, which beats the paragraph mark\'s w:pPr/w:rPr, which beats the w:pStyle chain, which beats docDefaults',
    ],
    cannot_detect: [
      'a w:basedOn chain deeper than MAX_STYLE_CHAIN_DEPTH (32) or containing a cycle — resolution stops there and reports what it resolved, so a pathological styles.xml degrades rather than hangs',
      'theme colours (w:themeColor) that resolve to the background through theme1.xml — the theme part is not resolved, so a themed white is read as "no explicit colour"',
      'text hidden behind an opaque drawing or image that covers it — this reads the text layer, not the rendering',
      'VML/DrawingML shapes positioned off-page (only w:framePr paragraph frames are read)',
      'a .doc, a Pages/Google-Docs export, or an encrypted file — the archive does not open, and the result reports styling_inspected: false rather than "clean"',
    ],
  },
  pdf: {
    detects: [
      'rendered text size ≤ 2pt, from the text item\'s own transform',
      'text items positioned outside the page box — off-canvas',
      'items consisting ENTIRELY of zero-width or control code points',
    ],
    cannot_detect: [
      'WHITE-ON-WHITE. Fill colour is set by graphics-state operators (sc/scn/rg/g) and pdf.js\'s getTextContent() does not expose the fill colour of a text item. Recovering it means walking getOperatorList() and re-implementing graphics-state tracking to correlate operators back to text items, which pdf.js does not correlate for us.',
      'TEXT RENDER MODE 3 (invisible text, the "3 Tr" OCR-layer idiom). Same reason: render mode is a graphics-state operator, absent from getTextContent().',
      'text covered by an opaque vector fill or image drawn over it.',
    ],
    boundary_note:
      'PDF detection is BEST EFFORT and this is an accepted R5 boundary, not a defect to be silently carried: the two undetectable vectors are named above, the DOCX path (the format the product actually receives most CVs in) is complete, and a PDF whose text layer is unreadable already routes to the ScannedPdfError paste steer.',
  },
  text: {
    detects: ['nothing — a .txt/.md upload carries no styling, so there is nothing to detect and nothing to miss'],
    cannot_detect: ['anything; styling_inspected is false and the caller must not read an empty annotation list as a clean bill of health'],
  },
} as const;
